import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { User } from '../_models/user.model';
import { Permission } from '../_models/permission.model';
import { Role } from '../_models/role.model';
import { catchError, map } from 'rxjs/operators';
import { QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { environment } from '../../../../environments/environment';
import { UserDataModel } from 'app/core/ana/authorization/models/user-data-model';
import { CookiesService } from './cookies.service';


@Injectable()
export class AuthService {

    /**
     * Logout url
     */
    private SIGNOUT_URL = environment.ipserver + 'AnA-backend/public/signOut';

    /**
     * Login url
     */
    private API_USERS_URL = environment.ipserver + 'AnA-backend/public/login';

    /**
     * Get user url
     */
    // private API_USERS_TOKEN_URL = environment.ipserver + 'AnA-backend/private/getUser';

    /**
     * Permissions url
     */
    private API_PERMISSION_URL = 'api/permissions';

    /**
     * Roles url
     */
    private API_ROLES_URL = 'api/roles';
    private userData: User;

    constructor(
        private http: HttpClient,
        private cookiesService: CookiesService
    ) { }

    /**
     * Method that authenticates an user by calling login rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-11-14
     * @param username: string that contains the user's username
     * @param password: string that contains the user's password
     * @return object observable that contains an authenticated user
     */
    public login(username: string, password: string): Observable<UserDataModel | string> {
        return this.http.post<UserDataModel>(this.API_USERS_URL, { username, password });
    }

    /**
     * End the session of a specified user or all the sessions for a user
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-09-29
     */
    public logout(username: string, sendDate: boolean = false): Observable<boolean> {
        const userData: UserDataModel = JSON.parse(this.cookiesService.getCookie('_remCred_', true));
        const paramsForReq = new HttpParams()
            .set('signInDate', sendDate ? userData.signInDate : '')
            .set('username', userData ? userData.username : username);
        const type = 'text' as 'json';
        return this.http.put<boolean>(this.SIGNOUT_URL, '', { params: paramsForReq, responseType: type });
    }

    getUserByToken(): Observable<User> {
        // const userToken = localStorage.getItem(environment.authTokenKey);
        /**
         * Consume service that obtains an user by token
         * @author Castor Transformación Digital - Juan G. Pavas
         * @since 2019-12-11
         */
        // const httpHeaders = new HttpHeaders()
        //     .set('Content-Type', 'application/json')
        //     .set('Authorization', userToken);
        // return this.http.get<User>(API_USERS_TOKEN_URL, { headers: httpHeaders })
        //     .pipe(
        //         map((res: User) => {
        //             res.firstname = res.fullname.split(' ')[0];
        //             return res;
        //         })
        //     );
        const userData = this.cookiesService.getCookie('_remCred_', true);
        if (userData) {
            this.userData = JSON.parse(userData);
        }
        return of(this.userData);
    }

    public setUser(data: any) {
        this.userData = data;
        const fn = (this.userData.fullname && String(this.userData.fullname).trim() !== '') ? String(this.userData.fullname).trim() : (this.userData.username || '');
        this.userData.firstname = fn ? fn.split(' ')[0] : '';
    }

    /**
     * Single source for display name across the app (CnC, confirmation, errors, etc.).
     * Prefer this over reading localStorage directly so logic stays in one place.
     * @returns fullname when set at login, otherwise username, otherwise empty string.
     */
    public getStoredFullname(): string {
        return (typeof localStorage !== 'undefined')
            ? (localStorage.getItem('fullname') || localStorage.getItem('username') || '').trim() || ''
            : '';
    }

	public getUser() {
        return this.userData;
    }

    public register(user: User): Observable<any> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<User>(this.API_USERS_URL, user, { headers: httpHeaders })
            .pipe(
                map((res: User) => {
                    return res;
                }),
                catchError(err => {
                    return null;
                })
            );
    }

    /**
     * Submit forgot password request
     *
     * @param {string} email
     * @returns {Observable<any>}
     */
    public requestPassword(email: string): Observable<any> {
        return this.http.get(this.API_USERS_URL + '/forgot?=' + email)
            .pipe(catchError(this.handleError('forgot-password', []))
            );
    }


    public getAllUsers(): Observable<User[]> {
        return this.http.get<User[]>(this.API_USERS_URL);
    }

    public getUserById(userId: number): Observable<User> {
        return this.http.get<User>(this.API_USERS_URL + `/${userId}`);
    }


    // DELETE => delete the user from the server
    public deleteUser(userId: number) {
        const url = `${this.API_USERS_URL}/${userId}`;
        return this.http.delete(url);
    }

    // UPDATE => PUT: update the user on the server
    public updateUser(user: User): Observable<any> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.put(this.API_USERS_URL, user, { headers: httpHeaders });
    }

    // CREATE =>  POST: add a new user to the server
    public createUser(user: User): Observable<User> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<User>(this.API_USERS_URL, user, { headers: httpHeaders });
    }

    // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
    // items => filtered/sorted result
    public findUsers(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<QueryResultsModel>(this.API_USERS_URL + '/findUsers', queryParams, { headers: httpHeaders });
    }

    // Permission
    public getAllPermissions(): Observable<Permission[]> {
        return this.http.get<Permission[]>(this.API_PERMISSION_URL);
    }

    public getRolePermissions(roleId: number): Observable<Permission[]> {
        return this.http.get<Permission[]>(this.API_PERMISSION_URL + '/getRolePermission?=' + roleId);
    }

    // Roles
    public getAllRoles(): Observable<Role[]> {
        return this.http.get<Role[]>(this.API_ROLES_URL);
    }

    public getRoleById(roleId: number): Observable<Role> {
        return this.http.get<Role>(this.API_ROLES_URL + `/${roleId}`);
    }

    // CREATE =>  POST: add a new role to the server
    public createRole(role: Role): Observable<Role> {
        // Note: Add headers if needed (tokens/bearer)
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<Role>(this.API_ROLES_URL, role, { headers: httpHeaders });
    }

    // UPDATE => PUT: update the role on the server
    public updateRole(role: Role): Observable<any> {
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.put(this.API_ROLES_URL, role, { headers: httpHeaders });
    }

    // DELETE => delete the role from the server
    public deleteRole(roleId: number): Observable<Role> {
        const url = `${this.API_ROLES_URL}/${roleId}`;
        return this.http.delete<Role>(url);
    }

    // Check Role Before deletion
    public isRoleAssignedToUsers(roleId: number): Observable<boolean> {
        return this.http.get<boolean>(this.API_ROLES_URL + '/checkIsRollAssignedToUser?roleId=' + roleId);
    }

    public findRoles(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
        // This code imitates server calls
        const httpHeaders = new HttpHeaders();
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<QueryResultsModel>(this.API_ROLES_URL + '/findRoles', queryParams, { headers: httpHeaders });
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     *
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    private handleError<T>(operation = 'operation', result?: any) {
        return (error: any): Observable<any> => {
            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to console instead

            // Let the app keep running by returning an empty result.
            return of(result);
        };
    }
}
